import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";

import type { ClientListRow } from "@/api/admin";
import type { PrimaryPersona } from "@/api/me";
import { useAuthStore, type ImpersonationContext } from "@/store/authStore";

/**
 * One way in and out of impersonation (MV2-844, ds/WhiteLabelSwap).
 *
 * Flipping `actingAs` changes the entire server scope — /me resolves to a
 * different branding block, /sites to a different site set, every energy
 * endpoint to a different client. Everything cached under the previous
 * context is stale, so entry AND exit invalidate with the same deny-list
 * predicate. Exit used to skip this (the banner's "Stop impersonation"
 * called setActingAs(null) alone), which is how the previous tenant's
 * favicon, logo and client rows survived the swap (R1 / R2 / R3).
 *
 * The only query that MUST stay stable across acting-as changes is the
 * unscoped client list — the TenantSwitcher's own list of targets.
 * Invalidating it would collapse the switcher to one row.
 */
export function isUnscopedClientsKey(key: QueryKey): boolean {
  return (
    Array.isArray(key) &&
    key[0] === "admin" &&
    key[1] === "clients" &&
    key[2] === "unscoped"
  );
}

export function useSwitchTenant(): (ctx: ImpersonationContext | null) => void {
  const qc = useQueryClient();
  const setActingAs = useAuthStore((s) => s.setActingAs);
  return (ctx) => {
    setActingAs(ctx);
    void qc.invalidateQueries({
      predicate: (query) => !isUnscopedClientsKey(query.queryKey),
    });
  };
}

/** MV2-1250: the "View as" picker's own user list survives the swap. */
function isViewAsCandidatesKey(key: QueryKey): boolean {
  return Array.isArray(key) && key[0] === "users" && key[1] === "view-as";
}

/** Drop every cached query except the two pickers' own lists. */
function resetForNewIdentity(qc: QueryClient): void {
  void qc.resetQueries({
    predicate: (query) =>
      !isUnscopedClientsKey(query.queryKey) &&
      !isViewAsCandidatesKey(query.queryKey),
  });
}

/** Who to view as, and the client they belong to. */
export interface ViewAsTarget {
  userId: string;
  name: string;
  persona: PrimaryPersona | null;
  clientId: string;
  clientName: string;
}

/**
 * One way into "View as" a real user (MV2-1250, MV2-1254; backend.md §108),
 * from the account menu, Admin → Users or ⌘K: one call sets Acting as to the
 * user's client and View as to the user, then lands on their home.
 *
 * The server answers as a different user while every queryKey still
 * carries the same `actingAs.clientId`, so invalidating would keep the
 * previous user's rows on screen until each refetch lands — a ghost-data
 * bleed. Reset instead: cached data is dropped, pages show their loading
 * state, active queries refetch under the new identity.
 *
 * The first switch records what to restore — the Acting as you had and the
 * page you were on; switching user to user keeps that first restore point.
 */
export function useStartViewAs(): (t: ViewAsTarget) => void {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  return (t) => {
    const s = useAuthStore.getState();
    const prevActingAs = s.viewingAs ? (s.viewingAs.prevActingAs ?? null) : s.actingAs;
    const returnTo = s.viewingAs?.returnTo ?? `${location.pathname}${location.search}`;
    const slug =
      qc
        .getQueryData<ClientListRow[]>(["admin", "clients", "unscoped"])
        ?.find((c) => c.id === t.clientId)?.slug ??
      (s.actingAs?.clientId === t.clientId ? s.actingAs.clientSlug : "");
    // Acting as first: a client change clears the previous View as.
    s.setActingAs({ clientId: t.clientId, clientName: t.clientName, clientSlug: slug });
    s.setViewingAs({
      userId: t.userId,
      name: t.name,
      persona: t.persona,
      prevActingAs,
      returnTo,
    });
    resetForNewIdentity(qc);
    navigate("/");
  };
}

/**
 * One click out (MV2-1254): back to yourself, with the Acting as you had
 * before and on the page you started from.
 */
export function useStopViewAs(): () => void {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return () => {
    const s = useAuthStore.getState();
    const v = s.viewingAs;
    if (!v) return;
    s.setViewingAs(null);
    s.setActingAs(v.prevActingAs ?? null);
    resetForNewIdentity(qc);
    navigate(v.returnTo ?? "/");
  };
}
